/**
 * Did the browser's GA4 hits actually reach Google?
 *
 * The Google tag stamps the last-hit timestamp into the _ga_<measurement id>
 * cookie when it SENDS a hit, not when Google ACKNOWLEDGES one. A collect
 * request that an extension blocks, that an edge cache answers, or that fails
 * in the Tag Gateway therefore leaves a cookie byte-for-byte identical to a
 * healthy one. That is why the server-side diagnostics cannot explain a
 * Measurement Protocol purchase GA4 files under "(not set)" while every
 * identifier on it looks correct: the information is not in the request the
 * shop receives.
 *
 * The browser does have it. Every request a page makes produces a resource
 * timing entry, and with the Tag Gateway the collect request is same-origin,
 * so its HTTP status is readable too. This module watches for those entries
 * and records what it saw.
 *
 * Read this as a ruling-out tool rather than a detection tool. A confirmed
 * delivery is high confidence and subtracts an order from the unexplained
 * pile, which is the point. The negative states say only what was observed,
 * because a blocked request and an opaque cross-origin success can look
 * alike from here.
 *
 * Diagnostics only. The verdict never enters a tracking payload, and it rides
 * the transient-identifiers request the plugin already makes on the cart and
 * checkout pages, so it costs no request of its own.
 *
 * @since 1.69.0
 */

const STORAGE_KEY = "ga4_hit_delivery"

/**
 * Anything GA4 measures through goes to a path ending in /g/collect, on the
 * shop's own domain with the Tag Gateway and on a Google host without it.
 */
const COLLECT_PATH = "/g/collect"

/**
 * How long to wait before concluding that nothing left the browser. The
 * Google tag sends its first hit as part of page load, so anything this side
 * of a few seconds is a shopper on a slow connection rather than a blocked
 * request.
 */
const SETTLE_MS = 4000

/**
 * Best to worst. A single successful hit answers the question, so a better
 * verdict always replaces a worse one and never the other way round. The
 * stored value is therefore the best thing that happened during the visit.
 *
 * confirmed   Google answered a collect request with a success status. Only
 *             same-origin requests (Tag Gateway) in browsers that expose
 *             responseStatus can produce this.
 * sent        A collect request was made, but its outcome is not readable.
 *             Every cross-origin collect looks like this, as does every
 *             collect in a browser without responseStatus.
 * failed      A collect request came back with an error status.
 * blocked     The Google tag loaded and no collect request was seen at all.
 * no_tag      The Google tag script itself never loaded, so no hit was ever
 *             built. A blocker, an extension or a consent gate stopped it.
 * unsupported There was no resource timing to read, so the probe has nothing
 *             to say. Recorded rather than left empty, because "we looked and
 *             could not tell" is worth distinguishing from "nobody looked".
 */
const RANK = {
	confirmed  : 6,
	sent       : 5,
	failed     : 4,
	blocked    : 3,
	no_tag     : 2,
	unsupported: 1,
}

/**
 * The three URL shapes the Google tag script takes. The Tag Gateway rewrites
 * the path, so the plain googletagmanager.com form is not enough. Same list
 * as first-event-confirmation.mjs checks.
 */
const TAG_URL_MARKERS = [ "gtag/js", "s=/gtag/js", "s=%2Fgtag%2Fjs" ]

const storedVerdict = () => {
	try {
		return pmw.retrieveData(STORAGE_KEY)
	} catch (e) {
		return undefined
	}
}

/**
 * The state of a verdict, without the HTTP status a failure carries.
 *
 * A failure is recorded as "failed:502" rather than "failed", because which
 * error it was decides what to look at: a 502 points at the Tag Gateway, a
 * 403 at something refusing the request. The status rides inside the one
 * string so the value stays a single stable scalar, which is what keeps the
 * request that carries it deduplicated.
 *
 * @param {string|undefined} verdict
 * @return {string}
 */
const stateOf = verdict => ( "string" === typeof verdict ? verdict.split(":")[0] : "" )

/**
 * Called once whenever the verdict improves, so the platform layer can carry
 * the new answer to the server.
 *
 * The probe cannot reach for the transport itself: it lives in the
 * platform-agnostic core, and the request that carries the verdict is
 * WooCommerce's. So it announces, and whoever owns a transport subscribes.
 *
 * @type {Array<function(string): void>}
 */
const subscribers = []

/**
 * Keep the best verdict this browser session has produced.
 *
 * Writing only on an improvement is what keeps the transient-identifiers
 * request deduplicated: that request is skipped whenever its payload is
 * unchanged, so a value that moved on every page view would turn a
 * once-per-visit request into a per-page-view one.
 *
 * @param {string} verdict
 * @return {void}
 */
const record = verdict => {

	const current = RANK[stateOf(storedVerdict())]

	if (current && current >= RANK[stateOf(verdict)]) return

	try {
		pmw.storeData(STORAGE_KEY, verdict)
	} catch (e) {
		// A browser refusing storage is not worth breaking tracking over.
	}

	subscribers.forEach(subscriber => {
		try {
			subscriber(verdict)
		} catch (e) {
			// A subscriber that throws is its own problem, not the probe's.
		}
	})
}

/**
 * Whether the Google tag script itself was observed loading.
 *
 * This is what separates "the tag never ran" from "the tag ran and its hits
 * went nowhere". Without it both collapse into one bucket, and they have
 * entirely different causes and fixes.
 *
 * @return {boolean}
 */
const tagScriptLoaded = () => {

	const loaded = typeof pmw.getLoadedScriptUrls === "function" ? pmw.getLoadedScriptUrls() : []

	if (loaded.some(url => TAG_URL_MARKERS.some(marker => url.includes(marker)))) return true

	return "function" === typeof window.gtag
}

export const initGa4DeliveryProbe = () => {

	// Only meaningful where GA4 runs at all. Everywhere else the question the
	// probe answers does not arise.
	const measurementId = pmwDataLayer?.pixels?.google?.analytics?.ga4?.measurement_id

	if (!measurementId) return

	/**
	 * A collect request belonging to this shop's GA4 property.
	 *
	 * GA4 puts v=2&tid=<measurement id> in the query string for every
	 * transport it uses, sendBeacon POSTs included, so the tid check works
	 * throughout and keeps a second GA4 property on the same page out of the
	 * answer.
	 *
	 * @param {PerformanceEntry} entry
	 * @return {boolean}
	 */
	const isCollectEntry = entry =>
		"resource" === entry.entryType
		&& "string" === typeof entry.name
		&& entry.name.includes(COLLECT_PATH)
		&& entry.name.includes("tid=" + measurementId)

	/**
	 * Classify one collect entry.
	 *
	 * responseStatus is Chromium-only, and reads 0 when the browser will not
	 * disclose it, which is an absence and not an error. Reading that 0 as a
	 * failure would report every Safari and Firefox visitor as broken.
	 *
	 * @param {PerformanceResourceTiming} entry
	 * @return {string}
	 */
	const classify = entry => {

		const status = "number" === typeof entry.responseStatus ? entry.responseStatus : 0

		if (0 === status) return "sent"
		if (status >= 400) return "failed:" + status

		return "confirmed"
	}

	/**
	 * Subscribe to verdict improvements.
	 *
	 * The verdict is settled a few seconds into a page, while the request
	 * that carries it goes out on pmwLoad, which is earlier. Without a way to
	 * say "the answer is ready now", a visitor whose only cart or checkout
	 * page view is their last one reaches the order with no reading at all.
	 * reknihy.cz measured that at 17 of 290 orders on 1.69.0 (HS 6960).
	 *
	 * @param {function(string): void} subscriber
	 * @return {void}
	 */
	const onVerdict = subscriber => {

		if ("function" !== typeof subscriber) return

		subscribers.push(subscriber)
	}

	pmw.ga4DeliveryProbe = {verdict: storedVerdict, onVerdict}

	// Already answered in an earlier page view of this visit, and a confirmed
	// delivery cannot be improved on, so there is nothing left to watch.
	if ("confirmed" === stateOf(storedVerdict())) return

	if ("function" !== typeof window.PerformanceObserver) {
		record("unsupported")
		return
	}

	const supported = window.PerformanceObserver.supportedEntryTypes

	if (Array.isArray(supported) && !supported.includes("resource")) {
		record("unsupported")
		return
	}

	let observer
	let settleTimer

	/**
	 * Let go of everything. The probe attaches an observer, a timer and two
	 * listeners, and once the question is answered none of them has any work
	 * left to do, so all four are released together rather than left running
	 * for the rest of the page.
	 *
	 * @return {void}
	 */
	const stop = () => {

		if (observer) {
			observer.disconnect()
			observer = null
		}

		if (settleTimer) {
			clearTimeout(settleTimer)
			settleTimer = null
		}

		window.removeEventListener("resourcetimingbufferfull", onBufferFull)
		window.removeEventListener("pagehide", onPageHide)
	}

	/**
	 * Nothing arrived inside the window, so say which of the two silences
	 * this was. Both are observations, neither names a culprit.
	 *
	 * @return {void}
	 */
	const settle = () => {
		record(tagScriptLoaded() ? "blocked" : "no_tag")
		stop()
	}

	const handle = entries => {

		entries.filter(isCollectEntry).forEach(entry => record(classify(entry)))

		// The question is answered. Stop looking at every remaining request on
		// the page for the sake of an answer that can no longer change.
		if ("confirmed" === stateOf(storedVerdict())) stop()
	}

	/**
	 * An overflowing buffer silently discards entries, and a discarded collect
	 * entry is indistinguishable from one that never happened. Reporting
	 * "blocked" off a page that simply had more than 250 resources would be
	 * exactly the kind of confident wrong answer this diagnostic has already
	 * been corrected for twice.
	 *
	 * @return {void}
	 */
	function onBufferFull() {
		record("unsupported")
		stop()
	}

	/**
	 * A shopper who leaves before the window closes still produces a verdict,
	 * otherwise a fast checkout would report nothing at all.
	 *
	 * @return {void}
	 */
	function onPageHide() {
		if (!storedVerdict()) settle()
		stop()
	}

	window.addEventListener("resourcetimingbufferfull", onBufferFull)

	try {
		observer = new PerformanceObserver(list => handle(list.getEntries()))

		// buffered: true replays the entries recorded before this ran. The
		// Google tag fires its first hit early, often before the library has
		// finished starting up, and that first hit is the one carrying the
		// traffic source.
		observer.observe({type: "resource", buffered: true})
	} catch (e) {
		// Older browsers reject the options object form of observe(). Nothing
		// to fall back to, and nothing that should surface to a shopper.
		observer = null
		record("unsupported")
		stop()
		return
	}

	settleTimer = setTimeout(settle, SETTLE_MS)

	window.addEventListener("pagehide", onPageHide)
}
